import React, {
    FormEvent,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react'
import { useMutation } from '@tanstack/react-query'
import reactStringReplace from 'react-string-replace'
import styled from 'styled-components'
import { apiClient } from '../utils/clients'
import { TextField } from './Form/TextField'
import { themeVariables } from '../themes/themeVariables'
import '../css/Spinner.css'
import { getRegexMatches } from '../utils/references'
import { useUpdateProjectFocusedDocument } from '../state/ProjectFocusedDocument.state'

const Root = styled.div`
    display: grid;
    gap: 16px;
`

const FormRow = styled.form`
    display: flex;
    gap: 12px;
`

const QuestionFieldWrap = styled.div`
    flex: 1;
`

const AnswerBox = styled.div`
    background: ${themeVariables.colors.backgroundSurface};
    border: 1px solid ${themeVariables.colors.border};
    border-radius: 8px;
    padding: 16px;
    white-space: pre-wrap;
`

const LoadingRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${themeVariables.colors.secondary};
`

const LoadingSpinner = styled.span.attrs({
    className: 'spinner',
})`
    position: static;
    top: auto;
    left: auto;
    margin-top: 0;
    transform: none;
    width: 14px;
    height: 14px;
    border-width: 2px;
    border-color: ${themeVariables.colors.border};
    border-top-color: ${themeVariables.colors.secondary};
`

const LoadingText = styled.span`
    font-size: 14px;
`

const ErrorText = styled.p`
    color: ${themeVariables.colors.critical};
    margin: 0;
`

const SourcesList = styled.ol`
    margin: 12px 0 0;
    padding-left: 20px;
    list-style: decimal;
    list-style-position: outside;
`

const SourceListItem = styled.li`
    display: list-item;
    margin-top: 4px;
`

const SourceLink = styled.a`
    color: ${themeVariables.colors.secondary};
    text-decoration: underline;
    text-underline-offset: 2px;
`

interface ProjectAdhocQuestionsProps {
    projectId: string
}

interface AdhocReference {
    documentId: string
    title: string
}

type AdhocQuestionResult = Awaited<
    ReturnType<typeof apiClient.adhocQuestion>
> & {
    references?: AdhocReference[]
}

export const ProjectAdhocQuestions = ({
    projectId,
}: ProjectAdhocQuestionsProps) => {
    const [question, setQuestion] = useState('')
    const [answer, setAnswer] = useState<AdhocQuestionResult | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const updateFocusedDocument = useUpdateProjectFocusedDocument()

    const openDocumentSidebar = useCallback(
        async (documentId: string) => {
            try {
                const document = await apiClient.getDataRoomDocument(documentId)
                updateFocusedDocument(document)
            } catch (error) {
                setErrorMessage('Unable to open referenced document right now.')
            }
        },
        [updateFocusedDocument]
    )

    const askQuestion = useMutation({
        mutationFn: async (nextQuestion: string) =>
            apiClient.adhocQuestion(projectId, { question: nextQuestion }),
        onSuccess: (result) => {
            setAnswer(result)
            setErrorMessage(null)
        },
        onError: () => {
            setErrorMessage('Unable to answer your question right now.')
        },
    })

    const trimmedQuestion = useMemo(() => question.trim(), [question])

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (!trimmedQuestion || askQuestion.isPending) {
                return
            }

            setErrorMessage(null)
            setAnswer(null)
            await askQuestion.mutateAsync(trimmedQuestion)
        },
        [askQuestion, trimmedQuestion]
    )

    const renderedAnswer = useMemo(() => {
        if (!answer?.answer) {
            return {
                content: null,
                sources: [],
            }
        }

        const matches = getRegexMatches(answer.answer)
        const referenceByDocumentId = new Map(
            (answer.references || []).map((reference) => [
                reference.documentId,
                reference,
            ])
        )
        const sourceIndexByDocumentId = new Map<string, number>()
        const sources: AdhocReference[] = []

        let formattedAnswer: ReactNode[] = [answer.answer]
        matches.forEach((match) => {
            const reference = referenceByDocumentId.get(match.documentId)
            if (!reference) {
                return
            }

            if (!sourceIndexByDocumentId.has(match.documentId)) {
                sourceIndexByDocumentId.set(
                    match.documentId,
                    sources.length + 1
                )
                sources.push(reference)
            }
            const sourceIndex = sourceIndexByDocumentId.get(match.documentId)!

            formattedAnswer = reactStringReplace(
                formattedAnswer,
                match.originalText,
                () => `(Ref. ${sourceIndex})`
            )
        })

        return {
            content: formattedAnswer,
            sources,
        }
    }, [answer])

    return (
        <Root>
            <h3 className="m-b-0">Ask the data room</h3>
            <FormRow onSubmit={handleSubmit}>
                <QuestionFieldWrap>
                    <TextField
                        value={question}
                        onChange={(event) => setQuestion(event.target.value)}
                        placeholder="Ask a quick question about the project"
                        disabled={askQuestion.isPending}
                    />
                </QuestionFieldWrap>
            </FormRow>
            {askQuestion.isPending && (
                <LoadingRow
                    role="status"
                    aria-live="polite"
                    aria-label="Answering question"
                >
                    <LoadingSpinner />
                    <LoadingText>Answering...</LoadingText>
                </LoadingRow>
            )}
            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
            {renderedAnswer.content && (
                <AnswerBox>
                    {renderedAnswer.content}
                    {renderedAnswer.sources.length > 0 && (
                        <SourcesList>
                            {renderedAnswer.sources.map((source) => (
                                <SourceListItem key={source.documentId}>
                                    <SourceLink
                                        href=""
                                        onClick={(event) => {
                                            event.preventDefault()
                                            void openDocumentSidebar(
                                                source.documentId
                                            )
                                        }}
                                    >
                                        {source.title}
                                    </SourceLink>
                                </SourceListItem>
                            ))}
                        </SourcesList>
                    )}
                </AnswerBox>
            )}
        </Root>
    )
}
